import { Types } from '../../index'
import { IAnyObject } from '../../interfaces'
import { Device, parseDevice, parseHistory, parseRules, parseSensor, Shipment } from '../../models'
import Rules from '../../models/Rules'
import { Account, TProperty } from '../../types'
import { IMqttAuth, parseMqttAuth } from '../MQTT'
import { ApiCore } from './ApiCore'

type TPHistoryReport = {
    shipments: string[]
    device: string
    sensors: { [key: string]: string }
    timezone: string
    format: string
    start?: number
    end?: number
    header: string
}

type TGetShipmentsListResponse = {
    items: {
        data: Types.TSmallShipment
    }[]
    totalItems: number
}

type TGetPalletsResponse = {
    items: Types.Pallet[]
    totalItems: number
}

type TPutPalletPayload = {
    IsMarkedCompleted?: number | undefined
    newLocation?: string | undefined
    physicalPalletNumber?: string | undefined
    physicalCart1Number?: string | undefined
    physicalCart2Number?: string | undefined
    AssignedDriver?: string | undefined
}

export class WidgetsApi {
    private static _apiClient: ApiCore

    public static get apiClient(): ApiCore {
        if (!WidgetsApi._apiClient) {
            throw new Error('API Client is not initiated')
        }

        return WidgetsApi._apiClient
    }

    public static set apiClient(client: ApiCore) {
        WidgetsApi._apiClient = client
    }

    public static getAccount(): Promise<Account> {
        return WidgetsApi.apiClient.get('account', {}).then(response => response as Account)
    }

    public static getShipmentByOrderId(id: string): Promise<Shipment> {
        return WidgetsApi.apiClient.get(`shipment-by-order-id/${id}`, {}).then(response => new Shipment(response))
    }

    public static getShipmentById(id: string, withManufacturingLabels: boolean | undefined): Promise<Shipment> {
        return WidgetsApi.apiClient
            .get(`shipment/${id}`, { withManufacturingLabels })
            .then(response => new Shipment(response))
    }

    public static async generateReport(
        shipmentId: string,
        deviceId: string,
        type: string,
        endDate?: number,
        startDate?: number
    ): Promise<string> {
        return WidgetsApi.apiClient
            .post(`shipment/${shipmentId}/device/${deviceId}/report`, { type, endDate, startDate })
            .then(response => (response as { [key: string]: any }).key as string)
    }

    public static async generateHistoryReport(data: TPHistoryReport): Promise<string> {
        data.start = data?.start && !isNaN(data.start) && data?.start > 1 ? data.start - 1 : data.start // can't be simplified!!!
        data.end = data?.end && !isNaN(data.end) && data?.end > 1 ? data.end + 1 : data.end; // Widget API use strings. Example  "2">"16234343545344" it got error in DynamoDB.

        return WidgetsApi.apiClient
            .post('report/history', data)
            .then(response => (response as { [key: string]: any }).key as string)
    }

    public static async getDeviceHistory(
        shipmentId: string,
        deviceId: string,
        start: number,
        end: number,
        whatevers: string,
        deviceType: string,
        lastKey?: string
    ): Promise<{ history: Map<string, TProperty[]>; newLastKey: Map<string, string> }> {
        const query = {
            start: start > 1 ? start - 1 : start, // can't be simplified!!!
            end: end > 1 ? end + 1 : end, // Widget API use strings. Example  "2">"16234343545344" it got error in DynamoDB.
            whatevers,
            lastKey: lastKey || ''
        }

        const response: IAnyObject = await WidgetsApi.apiClient.get(
            `shipment/${shipmentId}/device/${deviceId}/sensors/history`,
            query
        )
        const responseData = response?.data as IAnyObject
        const lastKeys: Map<string, string> = new Map<string, string>()
        const sensorHistory: Map<string, TProperty[]> = new Map<string, TProperty[]>()

        Object.keys(response.data as IAnyObject).forEach((sensorName: string) => {
            const sensor = responseData[sensorName] as IAnyObject
            const sensorData = sensor.data as IAnyObject[]

            sensorHistory.set(
                parseSensor(sensorName, deviceType, true),
                sensorData.map((item: any) => parseHistory(item, sensorName, deviceType, response.battId as string))
            )
            lastKeys.set(
                parseSensor(sensorName, deviceType, true),
                (sensor.lastKey as string) ?? (response.lastKey as string)
            )
        })

        return Promise.resolve({
            history: sensorHistory,
            newLastKey: lastKeys
        })
    }

    public static async getDevices(query: any): Promise<{ itemsCounts: any; devices: Device[] }> {
        return WidgetsApi.apiClient.get('devices', query).then(response =>
            Promise.resolve({
                itemsCounts: response.itemsCounts,
                devices: response.items.map(parseDevice)
            })
        )
    }

    public static async getReport(reportName: string): Promise<string> {
        return WidgetsApi.apiClient.get('report', { reportName }).then(response => response.url)
    }

    public static async getRules(shipmentId: string, deviceId: string, type?: string): Promise<Rules> {
        return WidgetsApi.apiClient.get(`shipment/${shipmentId}/device/${deviceId}/rules`).then(response => {
            const data = response.reduce(
                (obj: string[][], item: string) => ({
                    ...obj,
                    [item['RuleType']]: item['Value']
                }),
                {}
            )

            return parseRules(data, type || '', deviceId)
        })
    }

    public static async getShipments(query: IAnyObject): Promise<Shipment[]> {
        const res = (await WidgetsApi.apiClient.get('shipments', query)) as { items: IAnyObject[] }

        return res.items.map(_ => new Shipment(_))
    }

    public static async getShipmentsList(
        query: Partial<Types.TLoadShipmentsQuery>
    ): Promise<{ shipments: Types.TSmallShipment[]; total: number }> {
        const res = ((await WidgetsApi.apiClient.get('shipments', query)) as unknown) as TGetShipmentsListResponse

        return {
            shipments: res.items.map(_ => _.data),
            total: res.totalItems
        }
    }

    public static async putRules(shipmentId: string, deviceId: string, data: any): Promise<void> {
        return WidgetsApi.apiClient.put(`shipment/${shipmentId}/device/${deviceId}/rules`, data)
    }

    public static async submitAlarm(shipmentId: string, deviceId: string, query: any): Promise<any> {
        return WidgetsApi.apiClient.delete(`shipment/${shipmentId}/device/${deviceId}/alarm`, query)
    }

    public static async submitReason(shipmentId: string, deviceId: string, data: any): Promise<any> {
        return WidgetsApi.apiClient.put(`shipment/${shipmentId}/device/${deviceId}/alarm`, data)
    }

    public static async getConfig(): Promise<IAnyObject> {
        return WidgetsApi.apiClient.get('config', { version: 'v2' }) as Promise<IAnyObject>
    }

    public static async getMqttAuth(): Promise<IMqttAuth> {
        return WidgetsApi.apiClient.get('auth/mqtt').then(response => Promise.resolve(parseMqttAuth(response)))
    }

    public static async isVirtualShipment(shipmentId: string): Promise<boolean> {
        return WidgetsApi.apiClient
            .get(`shipment/${shipmentId}/is-virtual`)
            .then(response => (response as { [key: string]: any }).isVirtual as boolean)
    }

    public static async getPallets(isMovement?: boolean): Promise<{ pallets: Types.Pallet[]; total: number }> {
        const res = ((await WidgetsApi.apiClient.get('pallets', { isMovement })) as unknown) as TGetPalletsResponse

        return {
            pallets: res.items,
            total: res.totalItems
        }
    }

    public static async getPalletsByBatchId(batchId: string, query: IAnyObject): Promise<any[]> {
        return WidgetsApi.apiClient.get(`pallets/${batchId}`, query)
    }

    public static async updatePallet(palletId: string, payload: TPutPalletPayload): Promise<any> {
        return WidgetsApi.apiClient.put(`pallet/${palletId}`, payload)
    }
}
